import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Typography from '../Typography';
import Button from '../Button/Button';
import axiosClient from '../../api/axiosClient';
import Drawer from './Drawer';
import { theme } from '../../theme';
import { statusTagVariants } from '../../utils/statusColors';
import Tag from '../Tag';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import ChevronUpIcon from '../Icons/ChevronUpIcon';
import ChevronRightIcon from '../Icons/ChevronRightIcon';
import CloseIcon from '../Icons/CloseIcon';
import HelmetIcon from '../Icons/HelmetIcon';
import { useAttendeeStateTranslations } from '../../constants/attendeeStates';

const AttendeeEventsDrawer = ({
  isOpen,
  onClose,
  attendeeId,
  attendeeName,
  attendeeEmail,
  attendeeAvatar,
  anchor = 'right',
  onNextAttendee,
  onPreviousAttendee,
  hasMultipleAttendees = false,
  width
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation('hostedEvents');
  const { getStateLabel } = useAttendeeStateTranslations();

  const fetchAttendeeEvents = async () => {
    if (!attendeeId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axiosClient.get(`/api/events/attendee/${attendeeId}/events`);

      if (response.data && response.data.success) {
        setEvents(response.data.events || []);
      } else {
        setError(t('drawer.errorFetchingEvents'));
      }
    } catch (err) {
      console.error('Error fetching attendee events:', err);
      setError(t('drawer.errorLoadingEvents'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && attendeeId) {
      fetchAttendeeEvents();
    }
  }, [isOpen, attendeeId]);



  return (
    <Drawer anchor={anchor} open={isOpen} onClose={onClose} width={width}>
      <DrawerHeader>
        {hasMultipleAttendees && (
          <NavigationButtons>
            <Button $iconOnly={true} $variant="ghost" onClick={onPreviousAttendee} title={t('drawer.previousAttendee')}>
              <ChevronUpIcon fill={theme.colors.defaultMain} width={20} height={20} />
            </Button>
            <Button $iconOnly={true} $variant="ghost" onClick={onNextAttendee} title={t('drawer.nextAttendee')}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={20} height={20} />
            </Button>
          </NavigationButtons>
        )}
        <Button $iconOnly={true} onClick={onClose} $variant="ghost">
          <CloseIcon fill={theme.colors.defaultMain} width={20} height={20} />
        </Button>
      </DrawerHeader>
      <UserDetailsWrapper>
        <UserDetailsLink to={`/user/${attendeeId}`}>
          <UserAvatar>
            {attendeeAvatar ? (
              <AvatarImage src={attendeeAvatar} alt={attendeeName} />
            ) : (
              <AvatarPlaceholder>
                <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
              </AvatarPlaceholder>
            )}
          </UserAvatar>
          <UserData>
            <Typography as="h2" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
              {attendeeName}
            </Typography>
            <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {attendeeEmail}
            </Typography>
          </UserData>
          <ChevronContainer>
            <ChevronRightIcon fill={theme.colors.defaultWeak} width={20} height={20} />
          </ChevronContainer>
        </UserDetailsLink>
        {/* <Divisor /> */}
      </UserDetailsWrapper>

      <DrawerBody>
        {loading ? (
          <LoadingState>
            <Typography as="p" $variant="body-1-regular">
              {t('drawer.loadingEvents')}
            </Typography>
          </LoadingState>
        ) : error ? (
          <ErrorState>
            <Typography as="p" $variant="body-1-regular">
              {error}
            </Typography>
            <RetryButton onClick={fetchAttendeeEvents}>
              <Typography as="span" $variant="body-2-medium">
                {t('drawer.retry')}
              </Typography>
            </RetryButton>
          </ErrorState>
        ) : events.length > 0 ? (
          <>

            <Typography as="h2" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('drawer.events')}
            </Typography>
            <EventsList>
              {events.map((event) => (
                <EventItem key={event._id}>
                  <EventHeader>
                    <EventTitle>
                      <Typography as="h3" $variant="body-1-semibold">
                        {event.title}
                      </Typography>
                      <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                        {format(new Date(event.startDate), 'dd MMM yyyy', { locale: es })}
                      </Typography>
                    </EventTitle>
                    <Tag $variant={statusTagVariants[event.status]}>
                      {getStateLabel(event.status)}
                    </Tag>
                  </EventHeader>
                  <EventDetails>
                    <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                      {t('drawer.enrolledOn')} {format(new Date(event.enrollmentDate), 'dd MMM yyyy', { locale: es })}
                    </Typography>
                  </EventDetails>
                </EventItem>
              ))}
            </EventsList>
          </>
        ) : (
          <EmptyState>
            <Typography as="p" $variant="body-1-regular">
              {t('drawer.noEventsRegistered')}
            </Typography>
          </EmptyState>
        )}
      </DrawerBody>
    </Drawer>
  );
};

AttendeeEventsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attendeeId: PropTypes.string,
  attendeeName: PropTypes.string,
  attendeeEmail: PropTypes.string,
  attendeeAvatar: PropTypes.string,
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  onNextAttendee: PropTypes.func,
  onPreviousAttendee: PropTypes.func,
  hasMultipleAttendees: PropTypes.bool
};

export default AttendeeEventsDrawer;

// Styled Components
const DrawerHeader = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const UserAvatar = styled.div`
  border-radius: ${({ theme }) => theme.radius.xs};
  flex-shrink: 0;
  height: 64px;
  overflow: hidden;
  width: 64px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 48px;
    width: 48px;
  }
`;

const AvatarImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const UserData = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0;
  justify-content: center;
`;

const AvatarPlaceholder = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  color: ${({ theme }) => theme.colors.defaultMain};
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const UserDetailsLink = styled(Link)`
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
    border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  }
`;

const UserDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  padding-bottom: 0;
`;

const ChevronContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 4px;
  margin-right: ${({ theme }) => theme.sizing.xs};
`;

const DrawerBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.md} ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.xxxl} ${({ theme }) => theme.sizing.sm};
`;

const EventsList = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 0 2px -1px ${({ theme }) => theme.fill.inverseAlphaMain16};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const EventItem = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  padding: ${({ theme }) => theme.sizing.sm};
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const EventHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;

const EventTitle = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
`;

const EventDetails = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;




const LoadingState = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.defaultWeak};
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.lg};
`;

const ErrorState = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.lg};
`;

const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  border-radius: 8px;
  cursor: pointer;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
    border-color: ${({ theme }) => theme.border.defaultStrong};
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.defaultWeak};
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.lg};
`;